/* src/components/admin/CreateDispatcherModal.tsx */
import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { adminDispatcherService, CreateDispatcherData } from "../../services/admin.dispatcher.service";
import { countryService, CountryFull } from "../../services/country.service";
import { utilsService, DniTypeObject, getDniCompositeKey, findDniTypeByCompositeKey, applyDniPrefix, getMaxLengthFromMask } from "../../services/utils.service";
import { errorService } from "../../services/error.service";
import useForm, { ValidationRules } from "../../hooks/useForm";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSave,
    faUser,
    faAddressCard,
    faGlobe,
    faIdCard,
    faEnvelope,
    faPhone,
    faMapMarkerAlt
} from "@fortawesome/free-solid-svg-icons";
import { formatPhoneNational, isValidPhone, getPhonePlaceholder } from "../../config/phone.utils";
import styles from "../../styles/components/CreateDispatcherModal.module.css";
import modalStyles from "../../styles/common/Modal.module.css";

interface CreateDispatcherModalProps {
    show: boolean;
    onHide: () => void;
    onSuccess: () => void;
}

interface CreateDispatcherFormState {
    firstName: string;
    lastName: string;
    iso2Country: string;
    kindOfDNI: string;
    dni: string;
    phoneNumber: string;
    email: string;
    state: string;
    zipCode: string;
}

const CreateDispatcherModal: React.FC<CreateDispatcherModalProps> = ({
    show,
    onHide,
    onSuccess,
}) => {
    const [countries, setCountries] = useState<CountryFull[]>([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [loading, setLoading] = useState(false);

    // ZipCode autocomplete states
    const [isValidatingZip, setIsValidatingZip] = useState(false);
    const [zipCodeValidated, setZipCodeValidated] = useState(false);
    const [zipCodeApiError, setZipCodeApiError] = useState<string | null>(null);

    const initialValues = React.useMemo<CreateDispatcherFormState>(() => ({
        firstName: "",
        lastName: "",
        iso2Country: "",
        kindOfDNI: "Licencia de conducir",
        dni: "",
        phoneNumber: "",
        email: "",
        state: "",
        zipCode: "",
    }), []);

    const validationRules = React.useMemo<ValidationRules<CreateDispatcherFormState>>(() => ({
        firstName: (val) => {
            if (!val?.trim()) return "El nombre es obligatorio";
            if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+$/.test(val)) return "El nombre solo debe contener letras";
            return null;
        },
        lastName: (val) => {
            if (!val?.trim()) return "El apellido es obligatorio";
            if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+$/.test(val)) return "El apellido solo debe contener letras";
            return null;
        },
        iso2Country: (val) => (!val ? "El país es obligatorio" : null),
        kindOfDNI: (val) => (!val ? "El tipo de documento es obligatorio" : null),
        dni: (val) => {
            if (!val?.trim()) return "La licencia es obligatoria";
            if (!/^[A-Za-z0-9]{6,18}$/.test(val)) return "La licencia debe tener entre 6 y 18 caracteres alfanuméricos";
            return null;
        },
        email: (val) => {
            if (!val?.trim()) return "El email es obligatorio";
            if (!/\S+@\S+\.\S+/.test(val)) return "Email inválido";
            return null;
        },
        phoneNumber: (val, allValues) => {
            if (!val?.trim()) return "El teléfono es obligatorio";
            if (!isValidPhone(val, allValues.iso2Country)) return "Número de teléfono inválido";
            return null;
        },
        state: (val) => (!val?.trim() ? "El estado es obligatorio" : null),
        zipCode: (val) => (!val?.trim() ? "El código postal es obligatorio" : null),
    }), []);

    const {
        values,
        errors,
        handleChange,
        handleSubmit,
        resetForm,
        setValues,
        setErrors
    } = useForm<CreateDispatcherFormState>(
        initialValues,
        validationRules
    );

    // Fetch sender countries list on mount
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                setLoadingCountries(true);
                const data = await countryService.getAllCountries();
                const senders = data.filter((c) => c.send === true);
                setCountries(senders);
                if (senders.length > 0) {
                    setValues((prev) => ({ ...prev, iso2Country: senders[0].iso2Country }));
                }
            } catch (error) {
                console.error("Error loading countries:", error);
            } finally {
                setLoadingCountries(false);
            }
        };
        fetchCountries();
    }, [setValues]);

    const validateZipCode = async (zip: string, country: string) => {
        if (zip && country) {
            setIsValidatingZip(true);
            setZipCodeValidated(false);
            setZipCodeApiError(null);
            try {
                const location = await utilsService.getLocationByZipcode({
                    iso2code: country,
                    zipcode: zip,
                });

                if (!location) {
                    setZipCodeApiError("Código postal inválido para el país seleccionado.");
                    setZipCodeValidated(false);
                } else {
                    setZipCodeApiError(null);
                    setZipCodeValidated(true);
                    setValues((prev) => {
                        const newState = location.state || prev.state;
                        setErrors((prevErrors) => ({
                            ...prevErrors,
                            state: newState ? undefined : prevErrors.state,
                            zipCode: undefined,
                        }));
                        return {
                            ...prev,
                            state: newState,
                        };
                    });
                }
            } catch (error) {
                console.error("Error validating zip code:", error);
                setZipCodeApiError("Error al validar el código postal.");
                setZipCodeValidated(false);
            } finally {
                setIsValidatingZip(false);
            }
        } else {
            setZipCodeApiError(null);
            setZipCodeValidated(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (values.zipCode && values.iso2Country) {
                validateZipCode(values.zipCode, values.iso2Country);
            } else {
                setZipCodeValidated(false);
                setZipCodeApiError(null);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [values.zipCode, values.iso2Country]);

    useEffect(() => {
        if (show) {
            if (countries.length > 0) {
                setValues((prev) => ({ ...prev, iso2Country: countries[0].iso2Country }));
            }
        } else {
            resetForm();
            setZipCodeValidated(false);
            setZipCodeApiError(null);
        }
    }, [show, countries, resetForm, setValues]);

    const selectedCountryObj = React.useMemo(
        () => countries.find((c) => c.iso2Country === values.iso2Country) ?? null,
        [countries, values.iso2Country]
    );

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        const formatted = formatPhoneNational(val, values.iso2Country, selectedCountryObj?.phoneCode);
        setValues((prev) => ({ ...prev, phoneNumber: formatted }));

        const isVal = isValidPhone(formatted, values.iso2Country);
        setErrors((prev) => ({
            ...prev,
            phoneNumber: !formatted ? "El teléfono es obligatorio" : !isVal ? "Número de teléfono inválido" : undefined,
        }));
    };

    const handleDniChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setValues((prev) => ({ ...prev, dni: val }));

        const error = validationRules.dni(val, { ...values, dni: val });
        setErrors((prev) => ({ ...prev, dni: error || undefined }));
    };

    const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        setValues((prev) => ({ ...prev, iso2Country: val, dni: "", kindOfDNI: "Licencia de conducir", phoneNumber: "", zipCode: "", state: "" }));
        setErrors((prev) => ({ ...prev, dni: "", kindOfDNI: "", phoneNumber: "", zipCode: "", state: "" }));
        setZipCodeValidated(false);
        setZipCodeApiError(null);
    };

    const onSubmit = async () => {
        try {
            setLoading(true);

            // Double check Zipcode before submitting
            if (values.zipCode && values.iso2Country && !zipCodeValidated) {
                const location = await utilsService.getLocationByZipcode({
                    iso2code: values.iso2Country,
                    zipcode: values.zipCode,
                });
                if (!location) {
                    setZipCodeApiError("Código postal inválido para el país seleccionado.");
                    setLoading(false);
                    return;
                }
            }

            const fullPhone = selectedCountryObj?.phoneCode
                ? `${selectedCountryObj.phoneCode.startsWith("+") ? "" : "+"}${selectedCountryObj.phoneCode}${values.phoneNumber}`
                : values.phoneNumber;

            const payload: CreateDispatcherData = {
                firstName: values.firstName.trim(),
                lastName: values.lastName.trim(),
                iso2Country: values.iso2Country.toUpperCase(),
                kindOfDNI: "Licencia de conducir",
                dni: values.dni.trim(),
                status: 1,
                phoneNumber: fullPhone,
                email: values.email.trim(),
                state: values.state.trim(),
                zipCode: values.zipCode.trim(),
            };

            const response = await adminDispatcherService.createDispatcher(payload);

            if (response.statusCode === 200) {
                onHide();
                onSuccess();
                await Swal.fire({
                    icon: "success",
                    title: "¡Éxito!",
                    text: "El transportista ha sido registrado correctamente.",
                    confirmButtonText: "Aceptar",
                    confirmButtonColor: "var(--color-primary)",
                });
            } else {
                errorService.showErrorAlert(response, "Error al Crear Transportista");
            }
        } catch (error: any) {
            console.error("Error in CreateDispatcherModal:", error);
            errorService.showErrorAlert(error, "Error de Conexión");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="lg"
            className={styles.createDispatcherModal}
            contentClassName={styles.scaleIn}
            animation={false}
        >
            <Modal.Header closeButton>
                <Modal.Title as="h3" className={modalStyles.modalTitle}>
                    Registrar Nuevo Transportista
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <p className={styles.modalSubtitle}>
                    Completa la información básica del transportista para afiliarlo al sistema.
                </p>
                <Form onSubmit={handleSubmit(onSubmit)} id="create-dispatcher-form" noValidate>
                    <div className={styles.formGrid}>
                        <Form.Group>
                            <Form.Label>Nombre</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faUser} className={styles.inputIcon} />
                                <Form.Control
                                    type="text"
                                    name="firstName"
                                    placeholder="Nombre"
                                    value={values.firstName}
                                    onChange={handleChange}
                                    className={`${styles.createDispatcherModalInput} ${errors.firstName ? styles.inputError : ""}`}
                                    disabled={loading}
                                    maxLength={50}
                                />
                            </div>
                            {errors.firstName && <div className={styles.errorText}>{errors.firstName}</div>}
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Apellido</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faUser} className={styles.inputIcon} />
                                <Form.Control
                                    type="text"
                                    name="lastName"
                                    placeholder="Apellido"
                                    value={values.lastName}
                                    onChange={handleChange}
                                    className={`${styles.createDispatcherModalInput} ${errors.lastName ? styles.inputError : ""}`}
                                    disabled={loading}
                                    maxLength={50}
                                />
                            </div>
                            {errors.lastName && <div className={styles.errorText}>{errors.lastName}</div>}
                        </Form.Group>
                    </div>

                    <div className={styles.formGrid}>
                        <Form.Group>
                            <Form.Label>País</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faGlobe} className={styles.inputIcon} />
                                <Form.Select
                                    name="iso2Country"
                                    value={values.iso2Country}
                                    onChange={handleCountryChange}
                                    className={`${styles.createDispatcherModalInput} ${errors.iso2Country ? styles.inputError : ""}`}
                                    disabled={loading || loadingCountries}
                                >
                                    {countries.map((c) => (
                                        <option key={c.iso2Country} value={c.iso2Country}>
                                            {c.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>
                            {errors.iso2Country && <div className={styles.errorText}>{errors.iso2Country}</div>}
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Número de Licencia</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faAddressCard} className={styles.inputIcon} />
                                <Form.Control
                                    type="text"
                                    name="dni"
                                    placeholder="Número de licencia"
                                    value={values.dni}
                                    onChange={handleDniChange}
                                    className={`${styles.createDispatcherModalInput} ${errors.dni ? styles.inputError : ""}`}
                                    disabled={loading}
                                    maxLength={18}
                                />
                            </div>
                            {errors.dni && <div className={styles.errorText}>{errors.dni}</div>}
                        </Form.Group>
                    </div>

                    <div className={styles.formGrid}>
                        <Form.Group style={{ gridColumn: "span 2" }}>
                            <Form.Label>Correo Electrónico</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faEnvelope} className={styles.inputIcon} />
                                <Form.Control
                                    type="email"
                                    name="email"
                                    placeholder="Correo electrónico"
                                    value={values.email}
                                    onChange={handleChange}
                                    className={`${styles.createDispatcherModalInput} ${errors.email ? styles.inputError : ""}`}
                                    disabled={loading}
                                />
                            </div>
                            {errors.email && <div className={styles.errorText}>{errors.email}</div>}
                        </Form.Group>
                    </div>

                    <div className={styles.formGrid}>
                        <Form.Group style={{ gridColumn: "span 2" }}>
                            <Form.Label>Teléfono</Form.Label>
                            <div
                                className={`${styles.phoneInputWrapper} ${errors.phoneNumber ? styles.isInvalidWrapper : ""}`}
                                style={!values.iso2Country ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                            >
                                {values.iso2Country && (
                                    <div className={styles.phonePrefix}>
                                        <img
                                            src={`https://flagcdn.com/w40/${values.iso2Country.toLowerCase()}.png`}
                                            srcSet={`https://flagcdn.com/w80/${values.iso2Country.toLowerCase()}.png 2x`}
                                            width="20"
                                            height="14"
                                            alt={values.iso2Country}
                                            className={styles.phonePrefixFlag}
                                            loading="lazy"
                                            decoding="async"
                                        />
                                        {selectedCountryObj?.phoneCode && (
                                            <span className={styles.phonePrefixCode}>
                                                {selectedCountryObj.phoneCode.startsWith("+")
                                                    ? selectedCountryObj.phoneCode
                                                    : `+${selectedCountryObj.phoneCode}`}
                                            </span>
                                        )}
                                    </div>
                                )}
                                <Form.Control
                                    type="text"
                                    name="phoneNumber"
                                    placeholder={!values.iso2Country ? "Selecciona un país" : getPhonePlaceholder(values.iso2Country) || "Teléfono"}
                                    value={values.phoneNumber}
                                    onChange={handlePhoneChange}
                                    disabled={loading || !values.iso2Country}
                                    style={!values.iso2Country ? { cursor: "not-allowed" } : {}}
                                />
                            </div>
                            {errors.phoneNumber && <div className={styles.errorText}>{errors.phoneNumber}</div>}
                        </Form.Group>
                    </div>

                    <div className={styles.formGrid}>
                        <Form.Group>
                            <Form.Label>Código Postal</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faMapMarkerAlt} className={styles.inputIcon} />
                                <Form.Control
                                    type="text"
                                    name="zipCode"
                                    placeholder="Código postal"
                                    value={values.zipCode}
                                    onChange={handleChange}
                                    className={`${styles.createDispatcherModalInput} ${errors.zipCode ? styles.inputError : ""}`}
                                    disabled={loading}
                                />
                                {isValidatingZip && (
                                    <Spinner
                                        animation="border"
                                        size="sm"
                                        className={styles.inputSpinner}
                                        style={{ position: "absolute", right: "12px", top: "50%", marginTop: "-8px", color: "var(--color-primary)" }}
                                    />
                                )}
                            </div>
                            {errors.zipCode && <div className={styles.errorText}>{errors.zipCode}</div>}
                            {zipCodeApiError && <div className={styles.errorText}>{zipCodeApiError}</div>}
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Estado</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faGlobe} className={styles.inputIcon} />
                                <Form.Control
                                    type="text"
                                    name="state"
                                    placeholder="Estado"
                                    value={values.state}
                                    onChange={handleChange}
                                    className={`${styles.createDispatcherModalInput} ${errors.state ? styles.inputError : ""}`}
                                    disabled={loading || isValidatingZip}
                                    readOnly={zipCodeValidated}
                                />
                            </div>
                            {errors.state && <div className={styles.errorText}>{errors.state}</div>}
                        </Form.Group>
                    </div>
                </Form>
            </Modal.Body>

            <Modal.Footer className={styles.modalFooter}>
                <Button
                    variant="secondary"
                    onClick={onHide}
                    className={styles.secondaryBtn}
                    disabled={loading}
                >
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    type="submit"
                    form="create-dispatcher-form"
                    className={modalStyles.closeBtn}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Registrando...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faSave} className="me-2" /> Registrar
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CreateDispatcherModal;
